import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { parseDocs } from './parseDocs';
import {
  evenementInscriptionCreateSchema,
  evenementInscriptionSchema,
  type EvenementInscription,
  type EvenementInscriptionCreate,
  type EvenementInscriptionStatus,
} from '../schemas/evenementInscription';

const COLLECTION = 'evenementInscriptions';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): EvenementInscription {
  const data = raw as Record<string, unknown>;
  const createdAt =
    data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : (data.createdAt as string);
  const updatedAt =
    data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : (data.updatedAt as string | undefined) ?? createdAt;

  // Same null-stripping guard as the formation inscriptions repo — an admin
  // clearing an optional field may write `null` which Zod's `.optional()`
  // doesn't accept; coerce to undefined so per-doc parse never blanks the
  // whole list.
  const rawProfile = data.profile;
  const profile =
    rawProfile && typeof rawProfile === 'object'
      ? Object.fromEntries(
          Object.entries(rawProfile as Record<string, unknown>).filter(([, v]) => v !== null),
        )
      : rawProfile;

  return evenementInscriptionSchema.parse({ ...data, id, profile, createdAt, updatedAt });
}

export async function createEvenementInscription(
  input: EvenementInscriptionCreate & { memberUid?: string | null },
): Promise<string> {
  const { memberUid, ...rest } = input;
  const parsed = evenementInscriptionCreateSchema.parse(rest);
  const profile = { ...parsed.profile, email: parsed.profile.email.trim().toLowerCase() };
  const ref = await addDoc(collection(db(), COLLECTION), {
    ...parsed,
    profile,
    status: 'pending' as EvenementInscriptionStatus,
    payments: [],
    memberUid: memberUid ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function listEvenementInscriptions(): Promise<EvenementInscription[]> {
  const q = query(collection(db(), COLLECTION), orderBy('createdAt', 'desc'), limit(500));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function listEvenementInscriptionsByEvent(
  evenementId: string,
): Promise<EvenementInscription[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('evenementId', '==', evenementId),
    orderBy('createdAt', 'desc'),
    limit(500),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function listEvenementInscriptionsByMember(
  memberUid: string,
): Promise<EvenementInscription[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('memberUid', '==', memberUid),
    orderBy('createdAt', 'desc'),
    limit(100),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function getEvenementInscription(
  id: string,
): Promise<EvenementInscription | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

export async function updateEvenementInscription(
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteEvenementInscription(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
